/**
 * Responsive, touch friendly image lightbox.
 * This module requires PhotoSwipe, so make sure to include it before initializing.
 * http://photoswipe.com/documentation/getting-started.html
 *
 * Used in conjunction with styles/objects/_photoswipe.scss
 *
 * Markup example:
 * ```
 * <!-- image without meta data -->
 * <a class="en-photoswipe-trigger" href="image-large-1.jpg"><img src="thumbnail-1.jpg" alt="thumbnail" /></a>
 *
 * <!-- image with meta data -->
 * <a class="en-photoswipe-trigger" href="image-large-2.jpg" data-en-photoswipe-caption="My image title" data-en-photoswipe-description="My image description"><img src="thumbnail-2.jpg" alt="thumbnail" /></a>
 * </ul>
 * ```
 *
 * Implementation example:
 * ```
 * EN.PhotoSwipe.init();
 * ```
 *
 * @module PhotoSwipe
 * @author EN GARDE (David Hell)
 * @version 2.0
 *
 * @requires PhotoSwipe (http://photoswipe.com/documentation/getting-started.html)
 *
 * Changelog:
 *  2.0   rewrite without jquery
 * 	1.0		initial version
 */

import throttle from 'lodash/throttle';

import PhotoSwipePlugin from 'photoswipe/dist/photoswipe';
import PhotoSwipePluginUI from 'photoswipe/dist/photoswipe-ui-default';

class PhotoSwipe {

	static _defaults = {
		selectors: {
			trigger: '.en-photoswipe-trigger',
			scope: '.en-photoswipe-group',
			photoSwipeElement: '.pswp',
			photoSwipeBullets: '.pswp__bullets'
		},
		classNames: {
			bullet: 'pswp__bullet',
			active: 'en-is-active',
		},
		dataAttributes: {
			url: 'data-en-photoswipe-url',
			width: 'data-en-photoswipe-width',
			height: 'data-en-photoswipe-height',
			title: 'data-en-photoswipe-title',
			type: 'data-en-photoswipe-type',
			video: 'data-en-photoswipe-video',
		},
		photoSwipe: {
			// Core Options
			// see http://photoswipe.com/documentation/options.html
			index: 0,
			escKey: true,
			showHideOpacity: true,
			history: false,

			// Default UI Options
			shareEl: false,
			fullscreenEl: false,
			barsSize: {
				top: 0,
				bottom: 0
			},
			preload: [1,2]
		}
	};

	// Empty constructor - for legacy reasons everything happens in the init function.
	constructor() {

	}

	/**
   * Sets up all event listeners and sets options.
   * @memberof module:PhotoSwipe
   * @param {object} [settings] - Overrides defaults, see below.
   * @property {string} selector=".en-photoswipe-trigger[href]" - Element selector for the trigger elements.
   * @property {string} scope - Selector to group elements so that the lightbox doesn't include all elements on the page.
   * @property {string} container="body" - Element selector to place the lightbox in.
   * @property {object} classNames - Collection of class names that are set by the module.
   * @property {object} dataAttributes - Collection of data attribute names that are set by the module.
   */
	init( settings ) {

		this.options = Object.assign( {}, PhotoSwipe._defaults, settings );
		this.containerEl = document.querySelector( this.options.selectors.photoSwipeElement );

		if ( ! this.containerEl ) {
			console.error( 'PhotoSwipe.js cannot find the photoSwipeElement in the DOM. Make sure to add it before initializing.' );
			return false;
		}

		const options = this.options;

		// firefox > 59 triggers the click event twice – WTF!
		// the first of the two is triggered on the original element
		// this leads to problems when closing photoswipe
		// prevent that by throttling the function
		const _open = throttle( ( el ) => {

			const scopeEl = el.closest( options.selectors.scope ) || document.body;
			const triggerEls = scopeEl.querySelectorAll( options.selectors.trigger );
			const index = Array.from( triggerEls ).indexOf( el );

			this.open( triggerEls, index );
		}, 50, {
			leading: false,
			trailing: true,
		} );

		// open the lightbox on click
		document.addEventListener( 'click', ( evt ) => {
			if ( evt.target && evt.target.closest( this.options.selectors.trigger ) ) {
				evt.preventDefault();
				_open( evt.target.closest( this.options.selectors.trigger ) );
			}
		} );

		// Go to the image corresponding to the clicked bullet.
		const onBulletClick = ( evt ) =>  {
			const selector = '.' + this.options.classNames.bullet;
			if ( evt.target && evt.target.closest( selector ) ) {
				const bullet = evt.target.closest( selector );
				this.photoSwipeInstance.goTo( Array.from( bullet.parentElement.children ).indexOf( bullet ) );
			}
		}

		document.addEventListener( 'click', ( evt ) => onBulletClick( evt ) );
		document.addEventListener( 'touchstart', ( evt ) => onBulletClick( evt ) );
	}



	#getPhotoSwipeItem( element ) {

		const options = this.options;

		if ( 'video' === element.getAttribute( options.dataAttributes.type ) ) {
			return {
				html: element.getAttribute( options.dataAttributes.video ),
			};
		}

		return {
			src: element.getAttribute( options.dataAttributes.url ),
			w: element.getAttribute( options.dataAttributes.width ),
			h: element.getAttribute( options.dataAttributes.height ),
			title: element.getAttribute( options.dataAttributes.title )
		};
	}

	/**
   * Show PhotoSlide element. Make sure to add it to the DOM before.
   * @memberof module:PhotoSwipe
   * @param {NodeList} elements - Set of anchor elements to create the lightbox from.
   * @param {number} [startIndex=0] - (Zero-based) index of the element to be shown first.
   * @return {PhotoSwipe}
   */
	open( elements, index ) {
		if ( ! this.containerEl ) {
			console.error( 'PhotoSwipe.js cannot find the photoSwipeElement in the DOM. Make sure to add it before initializing.' );
			return;
		}

		const containerEl = this.containerEl;
		const photoSwipeItems = Array.from( elements ).map( ( element ) => this.#getPhotoSwipeItem( element ) );
		const photoSwipeOptions = Object.assign( {}, this.options.photoSwipe );

		if ( index ) {
			photoSwipeOptions.index = parseInt( index, 10 );
		}

		// Initialise PhotoSwipe Instance..
		this.photoSwipeInstance = new PhotoSwipePlugin( containerEl, PhotoSwipePluginUI, photoSwipeItems, photoSwipeOptions );
		this.photoSwipeInstance.init();
		this.photoSwipeInstance.listen( 'close', () => this.#refreshAllVideos() );

		this.#createBullets( photoSwipeItems.length, 7 );
		this.#onOpen();

		return this.photoSwipeInstance;
	}

	/**
   * Hide PhotoSlide element.
   * @memberof module:PhotoSwipe
   */
	close() {
		if ( this.photoSwipeInstance && typeof this.photoSwipeInstance.close === 'function' ) {
			this.photoSwipeInstance.close();
			this.photoSwipeInstance = null;
			this.#onClose();
		}
	}

	#refreshAllVideos() {

		// Refresh Video src.
		document.querySelectorAll( '.pswp__video' ).forEach( videoEl => {
			videoEl.setAttribute( 'src', videoEl.getAttribute( 'src' ) );
		} );
	}

	/**
   * Creates custom navigation bullets for the current PhotoSwipe element.
   * @param {number} bulletNumber - Number of bullets to create.
   * @return {jQuery}
   */
	#createBullets( bulletNumber ) {
		// Check if container is visiable and set.
		if ( ! this.containerEl || ! this.photoSwipeInstance ) {
			return;
		}

		// If BulletNumber is invalid.
		if ( typeof bulletNumber !== 'number' || bulletNumber < 2 ) {
			return;
		}

		const options = this.options;
		const bulletContainerEl = this.containerEl.querySelector( options.selectors.photoSwipeBullets );
		const currentIndex = this.photoSwipeInstance.getCurrentIndex();

		// Add bullets to HTML.
		let html = '';
		for ( let i = 0; i < bulletNumber; i++ ) {
			const className = options.classNames.bullet + ( i === currentIndex ? ' ' + options.classNames.active : '' );
			html += '<li class="' + className + '"></li>';
		}

		bulletContainerEl.innerHTML = html;

		// Set active bullet on change.
		this.photoSwipeInstance.listen( 'beforeChange', () => {
			// Add active class to active bullet and remove from all others.
			const newIndex = this.photoSwipeInstance.getCurrentIndex();
			bulletContainerEl.querySelectorAll( '.' + options.classNames.bullet ).forEach( ( bulletEl, index ) => bulletEl.classList.toggle( options.classNames.active, index === newIndex ) );
		} );

		return this.bulletContainerEl;
	}

	/**
   * Trigger
   *
   * @param int count
   */
	preloadImages( count, force = false ) {
		count = parseInt( count ) > 0 ? parseInt( count ) : 1;
		if ( this.imagesPreloaded < count || force ) {
			this.imagesPreloaded = count;

			document.querySelectorAll( this.options.selectors.scope ).forEach( ( scopeEl ) => {

				// Get all PhotoSwipe Items.
				const photoEls = scopeEl.querySelectorAll( this.options.selectors.trigger );
				Array.from( photoEls ).slice( 0, count ).forEach( ( photoEl ) => {
					// Get Item Data and preload image via src.
					const photoSwipeItem = this.#getPhotoSwipeItem( photoEl );
					if ( photoSwipeItem.src ) {
						const image = new Image();
						image.src = photoSwipeItem.src;
					}
				} );
			} );
		}
	}

	// Simple Event Handling.
	#customEvents = {}

	on( eventType, callback ) {
		this.#customEvents[ eventType ] = [].concat( callback );
	}

	off( eventType, callback ) {
		this.#customEvents[ eventType ] = this.#customEvents.filter( ( event ) => event !== callback );
	}

	#onOpen() {
		this.#customEvents['open'].forEach( callback => callback.call( this ) );
	}
	#onClose() {
		this.#customEvents['close'].forEach( callback => callback.call( this ) );
	}
}

// Show Error in case the plugin is not installed correctly.
if ( typeof PhotoSwipePlugin === 'undefined' || typeof PhotoSwipePluginUI === 'undefined' ) {
	console.error( 'PhotoSwipe.js requires the PhotoSwipe plugin. Make sure to include it before initializing.' );
}

export default new PhotoSwipe();